import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import { EventSubscriptionStatus, type BackInStockSubscriberToken } from '@sleed/bizcommerce-api';
import { Pill, type PillVariant } from '~/components/pill';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDateTime } from '~/lib/format-date';
import { useBackInStockSubscriptionsList } from '~/hooks/use-back-in-stock-subscriptions';

const STATUS: Record<number, { key: string; variant: PillVariant }> = {
	[EventSubscriptionStatus.PendingConfirmation]: { key: 'pendingConfirmation', variant: 'warning' },
	[EventSubscriptionStatus.PendingNotification]: { key: 'pendingNotification', variant: 'info' },
	[EventSubscriptionStatus.Notified]: { key: 'notified', variant: 'success' }
};

/** Back-in-stock subscribers — read-only: who waits for which product. Subscriptions are created by the storefront. */
export function BackInStockSubscriptionsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useBackInStockSubscriptionsList();

	const statusLabel = (s: BackInStockSubscriberToken) => {
		// A subscription switched off by hand (System → Event subscriptions) is never notified, whatever its status
		if (s.dsbl && s.st !== EventSubscriptionStatus.Notified) return t('catalog.backInStock.status.disabled');
		const status = s.st != null ? STATUS[s.st] : undefined;
		return status ? t(`catalog.backInStock.status.${status.key}`) : '—';
	};

	const columns = useMemo<SystemGridColumn<BackInStockSubscriberToken>[]>(
		() => [
			{
				key: 'email',
				header: t('catalog.backInStock.columns.email'),
				width: 'minmax(0,1fr)',
				sortValue: s => s.eml ?? '',
				exportValue: s => s.eml ?? '',
				render: s => (
					<a href={`mailto:${s.eml ?? ''}`} className="truncate text-sm font-medium text-foreground hover:underline">
						{s.eml || '—'}
					</a>
				)
			},
			{
				key: 'product',
				header: t('catalog.backInStock.columns.product'),
				width: 'minmax(0,1.4fr)',
				sortValue: s => s.pdnm ?? '',
				exportValue: s => s.pdnm ?? '',
				render: s => (
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-sm text-foreground">{s.pdnm || `#${s.pid ?? '—'}`}</span>
						<span className="truncate font-mono text-[11px] text-foreground-subtle">
							{s.pdcd ?? ''}
							{s.pdid != null ? ` · ${t('catalog.backInStock.variant', { id: s.pdid })}` : ''}
						</span>
					</div>
				)
			},
			{
				key: 'language',
				header: t('catalog.backInStock.columns.language'),
				width: '90px',
				align: 'center',
				defaultVisible: false,
				sortValue: s => s.lng ?? '',
				exportValue: s => s.lng ?? '',
				render: s => <span className="text-sm uppercase text-foreground-muted">{s.lng || '—'}</span>
			},
			{
				key: 'status',
				header: t('catalog.backInStock.columns.status'),
				width: '170px',
				align: 'center',
				sortValue: s => s.st ?? -1,
				exportValue: statusLabel,
				render: s => {
					const status = s.st != null ? STATUS[s.st] : undefined;
					const disabled = s.dsbl && s.st !== EventSubscriptionStatus.Notified;
					return <Pill variant={disabled ? 'neutral' : (status?.variant ?? 'neutral')}>{statusLabel(s)}</Pill>;
				}
			},
			{
				key: 'requested',
				header: t('catalog.backInStock.columns.requested'),
				width: '160px',
				sortValue: s => String(s.rdt ?? ''),
				exportValue: s => formatDateTime(s.rdt),
				render: s => <span className="text-sm text-foreground-muted">{formatDateTime(s.rdt)}</span>
			},
			{
				key: 'confirmed',
				header: t('catalog.backInStock.columns.confirmed'),
				width: '160px',
				defaultVisible: false,
				sortValue: s => String(s.cfdt ?? ''),
				exportValue: s => formatDateTime(s.cfdt),
				render: s => <span className="text-sm text-foreground-muted">{formatDateTime(s.cfdt)}</span>
			},
			{
				key: 'notified',
				header: t('catalog.backInStock.columns.notified'),
				width: '160px',
				sortValue: s => String(s.ndt ?? ''),
				exportValue: s => formatDateTime(s.ndt),
				render: s => <span className="text-sm text-foreground-muted">{formatDateTime(s.ndt)}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	return (
		<SystemGrid<BackInStockSubscriberToken>
			gridId="back-in-stock-subscriptions"
			title={t('catalog.backInStock.title')}
			rows={data ?? []}
			loading={isLoading}
			error={isError}
			onRetry={() => void refetch()}
			getRowId={s => s.id ?? `${s.eml}-${s.pid}-${s.pdid}`}
			columns={columns}
			searchPredicate={(s, q) => `${s.eml ?? ''} ${s.pdnm ?? ''} ${s.pdcd ?? ''}`.toLowerCase().includes(q)}
			defaultSort={{ key: 'requested', dir: 'desc' }}
			emptyIcon={<BellRing size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
			exportFilename="back-in-stock-subscriptions"
		/>
	);
}
